import { HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FiscalOnboardingFlagService } from '@common/services/fiscal-onboarding-flag.service';
import { UserService } from '@modules/auth/services';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, shareReplay, tap } from 'rxjs/operators';

import { FiscalBusinessPayload, FiscalChangeRequestPayload, FiscalHomePill, FiscalOnboardingResponse, FiscalOnboardingState, FiscalSignaturePayload, FiscalStatusPill } from '../models';
import { homePillFor, menuBadgeFor } from '../utils/fiscal-onboarding-status.util';
import { FiscalOnboardingApiService } from './fiscal-onboarding-api.service';

/**
 * Facade del onboarding fiscal: un único estado (`state$`) que alimentan la página Fiscal, el
 * asistente, la píldora del Home y el badge del menú. Toda mutación reemplaza el estado con la
 * respuesta del api (el servidor decide requisitos, pasos y permisos). Sin `localStorage`.
 */
@Injectable({ providedIn: 'root' })
export class FiscalOnboardingStateService {
    private readonly stateSubject = new BehaviorSubject<FiscalOnboardingState | null>(null);
    readonly state$: Observable<FiscalOnboardingState | null> = this.stateSubject.asObservable();

    private readonly loadingSubject = new BehaviorSubject<boolean>(false);
    readonly loading$ = this.loadingSubject.asObservable();

    /** Última carga fallida (la página muestra el error; el Home y el menú callan). */
    readonly loadError$ = new BehaviorSubject<boolean>(false);

    private inflight$: Observable<FiscalOnboardingState | null> | null = null;
    /** Tenant del estado cargado: al cambiar de sesión se descarta. */
    private loadedTenantId: string | null = null;

    /** Píldora de la cabecera del Home (solo dueño con el flag; nunca en `activo`). */
    readonly homePill$: Observable<FiscalHomePill | null> = this.state$.pipe(
        map(state => homePillFor(state)),
        distinctUntilChanged((a, b) => a?.label === b?.label && a?.tone === b?.tone && a?.chip === b?.chip),
    );

    /** Badge del ítem «Fiscal» del menú (todos los usuarios del tenant). */
    readonly menuBadge$: Observable<FiscalStatusPill | null> = this.state$.pipe(
        map(state => menuBadgeFor(state)),
        distinctUntilChanged((a, b) => a?.label === b?.label && a?.tone === b?.tone),
    );

    constructor(private api: FiscalOnboardingApiService, private userService: UserService, private fiscalOnboardingFlag: FiscalOnboardingFlagService) {}

    get snapshot(): FiscalOnboardingState | null {
        return this.stateSubject.getValue();
    }

    /** Flag PostHog `fiscal-recaudos` (fail-closed), el mismo que gatea la ruta. */
    get enabled(): boolean {
        return this.fiscalOnboardingFlag.canAccessSync();
    }

    /**
     * Carga el estado una vez por sesión (o de nuevo con `force`). Comparte la petición en vuelo
     * entre Home, menú y página. Nunca lanza: ante error emite `null` y marca `loadError$`.
     */
    load(force = false): Observable<FiscalOnboardingState | null> {
        const tenantId = this.userService.user$.getValue()?.tenantId ?? null;
        if (tenantId !== this.loadedTenantId) {
            // Solo al cambiar de sesión se descarta lo cargado (la primera carga no emite un `null` extra).
            if (this.loadedTenantId !== null) this.reset();
            this.loadedTenantId = tenantId;
        }
        if (!force && this.snapshot) return of(this.snapshot);
        if (this.inflight$) return this.inflight$;

        this.loadingSubject.next(true);
        this.inflight$ = this.api.getState$().pipe(
            tap(state => {
                this.loadError$.next(false);
                this.stateSubject.next(state);
            }),
            catchError(() => {
                this.loadError$.next(true);
                return of(null);
            }),
            finalize(() => {
                this.loadingSubject.next(false);
                this.inflight$ = null;
            }),
            shareReplay(1),
        );
        return this.inflight$;
    }

    refresh(): Observable<FiscalOnboardingState | null> {
        return this.load(true);
    }

    /** Lo llama el Home/menú: carga solo con el flag y con sesión; no repite si ya hay estado. */
    ensureLoaded(): void {
        if (!this.enabled || !this.userService.user$.getValue()?.tenantId) return;
        this.load().subscribe();
    }

    reset(): void {
        this.stateSubject.next(null);
        this.loadError$.next(false);
        this.inflight$ = null;
    }

    apply(state: FiscalOnboardingState): void {
        this.stateSubject.next(state);
    }

    // ── Mutaciones (cada una reemplaza el estado con la respuesta) ───────────────

    saveBusiness(payload: FiscalBusinessPayload): Observable<FiscalOnboardingState> {
        return this.api.saveBusiness$(payload).pipe(tap(state => this.apply(state)));
    }

    addPartner(): Observable<FiscalOnboardingState> {
        return this.api.addPartner$().pipe(tap(state => this.apply(state)));
    }

    removePartner(partnerId: string): Observable<FiscalOnboardingState> {
        return this.api.removePartner$(partnerId).pipe(tap(state => this.apply(state)));
    }

    /** El consumidor aplica el estado cuando llega el `HttpEventType.Response` (ver `applyUploadResponse`). */
    uploadDocument(docKey: string, file: File): Observable<HttpEvent<FiscalOnboardingResponse<FiscalOnboardingState>>> {
        return this.api.uploadDocument$(docKey, file);
    }

    applyUploadResponse(body: FiscalOnboardingResponse<FiscalOnboardingState> | null | undefined): void {
        if (body?.data) this.apply(body.data);
    }

    deleteDocument(docKey: string): Observable<FiscalOnboardingState> {
        return this.api.deleteDocument$(docKey).pipe(tap(state => this.apply(state)));
    }

    submit(): Observable<FiscalOnboardingResponse<FiscalOnboardingState>> {
        return this.api.submit$().pipe(tap(res => this.apply(res.data)));
    }

    resubmit(): Observable<FiscalOnboardingResponse<FiscalOnboardingState>> {
        return this.api.resubmit$().pipe(tap(res => this.apply(res.data)));
    }

    sign(payload: FiscalSignaturePayload): Observable<FiscalOnboardingResponse<FiscalOnboardingState>> {
        return this.api.signAuthorization$(payload).pipe(tap(res => this.apply(res.data)));
    }

    // ── Solicitud de cambio de perfil (el asistente en modo cambio edita `state.changeRequest`) ──

    openChangeRequest(payload: FiscalChangeRequestPayload): Observable<FiscalOnboardingState> {
        return this.api.openChangeRequest$(payload).pipe(tap(state => this.apply(state)));
    }

    updateChangeRequest(payload: FiscalChangeRequestPayload): Observable<FiscalOnboardingState> {
        return this.api.updateChangeRequest$(payload).pipe(tap(state => this.apply(state)));
    }

    saveChangeBusiness(payload: FiscalBusinessPayload): Observable<FiscalOnboardingState> {
        return this.api.saveChangeBusiness$(payload).pipe(tap(state => this.apply(state)));
    }

    addChangePartner(): Observable<FiscalOnboardingState> {
        return this.api.addChangePartner$().pipe(tap(state => this.apply(state)));
    }

    removeChangePartner(partnerId: string): Observable<FiscalOnboardingState> {
        return this.api.removeChangePartner$(partnerId).pipe(tap(state => this.apply(state)));
    }

    /** Como `uploadDocument`: el consumidor aplica el estado con `applyUploadResponse` al llegar la respuesta. */
    uploadChangeDocument(docKey: string, file: File): Observable<HttpEvent<FiscalOnboardingResponse<FiscalOnboardingState>>> {
        return this.api.uploadChangeDocument$(docKey, file);
    }

    deleteChangeDocument(docKey: string): Observable<FiscalOnboardingState> {
        return this.api.deleteChangeDocument$(docKey).pipe(tap(state => this.apply(state)));
    }

    submitChangeRequest(): Observable<FiscalOnboardingResponse<FiscalOnboardingState>> {
        return this.api.submitChangeRequest$().pipe(tap(res => this.apply(res.data)));
    }

    discardChangeRequest(): Observable<FiscalOnboardingState> {
        return this.api.discardChangeRequest$().pipe(tap(state => this.apply(state)));
    }
}
